import { lazy, Suspense, useEffect, useRef, useState, useCallback } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Toaster } from '@/components/ui/toaster'
import { ThemeProvider, useThemeContext } from '@/contexts/ThemeContext'
import { AuthProvider } from '@/hooks/AuthContext'
import { ProtectedRoute } from '@/components/layout/ProtectedRoute'
import { AppShell } from '@/components/layout/AppShell'
import { CreateAnnotationModal, IssueDetailPanel } from '@/components/annotations'
import { useUserPreferencesStore } from '@/stores/userPreferencesStore'

// Lazy-loaded page components for code splitting
const LoginPage = lazy(() => import('@/pages/auth/LoginPage').then(m => ({ default: m.LoginPage })))
const HomePage = lazy(() => import('@/pages/dashboard/HomePage').then(m => ({ default: m.HomePage })))
const StudyListPage = lazy(() => import('@/pages/studies/StudyListPage').then(m => ({ default: m.StudyListPage })))
const StudySettingsPage = lazy(() => import('@/pages/studies/StudySettingsPage').then(m => ({ default: m.StudySettingsPage })))
const StudyPageView = lazy(() => import('@/pages/studies/StudyPageView').then(m => ({ default: m.StudyPageView })))
const ModuleLibraryPage = lazy(() => import('@/pages/admin/ModuleLibraryPage').then(m => ({ default: m.ModuleLibraryPage })))
const DataUploadPage = lazy(() => import('@/pages/admin/DataUploadPage').then(m => ({ default: m.DataUploadPage })))
const CreateStudyPage = lazy(() => import('@/pages/admin/CreateStudyPage').then(m => ({ default: m.CreateStudyPage })))
const UserManagementPage = lazy(() => import('@/pages/admin/UserManagementPage').then(m => ({ default: m.UserManagementPage })))
const RoleManagementPage = lazy(() => import('@/pages/admin/RoleManagementPage').then(m => ({ default: m.RoleManagementPage })))
const SecuritySettingsPage = lazy(() => import('@/pages/admin/SecuritySettingsPage').then(m => ({ default: m.SecuritySettingsPage })))
const NotificationChannelsPage = lazy(() => import('@/pages/admin/NotificationChannelsPage').then(m => ({ default: m.NotificationChannelsPage })))
const ColumnLabelsPage = lazy(() => import('@/pages/admin/ColumnLabelsPage'))
const UserGuidePage = lazy(() => import('@/pages/admin/UserGuidePage').then(m => ({ default: m.UserGuidePage })))
const AuditLogsPage = lazy(() => import('@/pages/admin/AuditLogsPage'))
const BrandingPage = lazy(() => import('@/pages/admin/BrandingPage'))
const SystemMonitoringPage = lazy(() => import('@/pages/admin/SystemMonitoringPage').then(m => ({ default: m.SystemMonitoringPage })))
const UpdateChangeControlPage = lazy(() => import('@/pages/admin/UpdateChangeControlPage').then(m => ({ default: m.UpdateChangeControlPage })))
const RPackageManagerPage = lazy(() => import('@/pages/admin/RPackageManagerPage'))
const RFunctionLibraryPage = lazy(() => import('@/pages/admin/RFunctionLibraryPage'))
const ExternalFilesPage = lazy(() => import('@/pages/admin/ExternalFilesPage'))
const ComplianceDocsPage = lazy(() => import('@/pages/admin/ComplianceDocsPage'))
const CTLibraryPage = lazy(() => import('@/pages/admin/CTLibraryPage'))
const CodingLibraryPage = lazy(() => import('@/pages/admin/CodingLibraryPage'))
const AISuitePage = lazy(() => import('@/pages/admin/AISuitePage'))
const NarrativeSettingsPage = lazy(() => import('@/pages/admin/NarrativeSettingsPage'))
const SettingsPage = lazy(() => import('@/pages/settings').then(m => ({ default: m.SettingsPage })))
const IssuesListPage = lazy(() => import('@/pages/issues').then(m => ({ default: m.IssuesListPage })))
const SelfNotesPage = lazy(() => import('@/pages/issues').then(m => ({ default: m.SelfNotesPage })))
const QueryTrackingPage = lazy(() => import('@/pages/issues').then(m => ({ default: m.QueryTrackingPage })))
const PatientProfilePage = lazy(() => import('@/pages/patient-profile/PatientProfilePage').then(m => ({ default: m.PatientProfilePage })))
const NarrativeTrackerPage = lazy(() => import('@/pages/studies/NarrativeTrackerPage').then(m => ({ default: m.NarrativeTrackerPage })))
import { FeatureGate } from '@/components/common/FeatureGate'
import { Preloader } from '@/components/common/Preloader'
import { FEATURE_KEYS } from '@/hooks/useFeatures'
import { useStudyStore, Study } from '@/stores/studyStore'
import { useNavigationStore, NavigationItem } from '@/stores/navigationStore'
import { api } from '@/lib/api'
import { useAuthContext } from '@/hooks/AuthContext'
import { useAnalyticsTracking } from '@/hooks/useAnalyticsTracking'
import { useQuery } from '@tanstack/react-query'
import { getCurrentStudyFromSubdomain, isOnStudySubdomain, navigateToMainApp } from '@/lib/subdomain'

// Loading fallback for lazy-loaded pages
function PageLoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-[200px]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  )
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
})

/**
 * PreferencesHydrator — fetches user preferences on boot and syncs them
 * to ThemeContext and navigationStore. Also subscribes to preference changes
 * so that theme/sidebar updates from any source (Header toggle, PreferencesTab,
 * etc.) propagate to the correct UI systems.
 */
function PreferencesHydrator() {
  const { isAuthenticated } = useAuthContext()
  const { syncTheme } = useThemeContext()
  const fetchPreferences = useUserPreferencesStore((s) => s.fetchPreferences)
  const theme = useUserPreferencesStore((s) => s.preferences.theme)
  const sidebarCollapsed = useUserPreferencesStore((s) => s.preferences.sidebarCollapsed)
  const hasHydrated = useRef(false)

  // Fetch preferences from backend on auth
  useEffect(() => {
    if (isAuthenticated && !hasHydrated.current) {
      hasHydrated.current = true
      fetchPreferences()
    }
  }, [isAuthenticated, fetchPreferences])

  // Sync theme changes to ThemeContext
  useEffect(() => {
    syncTheme(theme)
  }, [theme, syncTheme])

  // Sync sidebar collapse to navigationStore
  useEffect(() => {
    const navStore = useNavigationStore.getState()
    if (navStore.isSidebarCollapsed !== sidebarCollapsed) {
      navStore.setSidebarCollapsed(sidebarCollapsed)
    }
  }, [sidebarCollapsed])

  // Subscribe to navigationStore sidebar toggles → push to preferences store
  useEffect(() => {
    let prevCollapsed = useNavigationStore.getState().isSidebarCollapsed
    const unsub = useNavigationStore.subscribe((state) => {
      if (state.isSidebarCollapsed !== prevCollapsed) {
        prevCollapsed = state.isSidebarCollapsed
        // Only sync if the value differs from what preferences store has
        const prefsCollapsed = useUserPreferencesStore.getState().preferences.sidebarCollapsed
        if (state.isSidebarCollapsed !== prefsCollapsed) {
          useUserPreferencesStore.getState().updatePreferences({
            sidebarCollapsed: state.isSidebarCollapsed,
          }).catch(() => {})
        }
      }
    })
    return unsub
  }, [])

  return null
}

// Component to validate and clear stale persisted study
function PersistedStudyValidator() {
  const { isAuthenticated } = useAuthContext()
  const { currentStudy, setCurrentStudy, removeFromRecent } = useStudyStore()

  // Validate persisted study exists in database
  const { isError } = useQuery({
    queryKey: ['validate-study', currentStudy?.studyId],
    queryFn: async () => {
      if (!currentStudy?.studyId) return null
      const response = await api.get(`/studies/${currentStudy.studyId}`)
      return response.data
    },
    enabled: !!currentStudy?.studyId && isAuthenticated,
    retry: false, // Don't retry - if it's 404, clear it immediately
  })

  // Clear stale study from store when it doesn't exist
  useEffect(() => {
    if (isError && currentStudy?.studyId) {
      console.warn(`Persisted study ${currentStudy.studyId} no longer exists, clearing from store`)
      removeFromRecent(currentStudy.studyId)
      setCurrentStudy(null)
    }
  }, [isError, currentStudy?.studyId, setCurrentStudy, removeFromRecent])

  return null
}

// Layout wrapper for authenticated pages
function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  )
}

// Component to load study from URL params and set in store
function StudyLoader({ children }: { children: React.ReactNode }) {
  const { studyId } = useParams<{ studyId: string }>()
  const { isAuthenticated } = useAuthContext()
  const { setCurrentStudy, currentStudy } = useStudyStore()
  const navigate = useNavigate()

  // Fetch study details
  const { data: study, isLoading, error } = useQuery({
    queryKey: ['study', studyId],
    queryFn: async () => {
      if (!studyId) return null
      const response = await api.get(`/studies/${studyId}`)
      return response.data as Study
    },
    enabled: !!studyId && isAuthenticated,
  })

  // Set current study when loaded
  useEffect(() => {
    if (study && study.studyId !== currentStudy?.studyId) {
      setCurrentStudy(study)
    }
  }, [study, currentStudy?.studyId, setCurrentStudy])

  // Handle study not found
  if (!isLoading && error && studyId && isAuthenticated) {
    return (
      <AuthenticatedLayout>
        <div className="flex flex-col items-center justify-center py-12">
          <h2 className="text-2xl font-bold mb-4">Study Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The study "{studyId}" does not exist or you don't have access.
          </p>
          <button
            className="text-primary hover:underline"
            onClick={() => navigate('/studies')}
          >
            Browse Studies
          </button>
        </div>
      </AuthenticatedLayout>
    )
  }

  return <>{children}</>
}

// Component to load study from subdomain and set in store
function SubdomainStudyLoader({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthContext()
  const { setCurrentStudy, currentStudy } = useStudyStore()

  // Get study ID from subdomain (e.g., abc-101.localhost -> ABC-101)
  const subdomainStudyId = getCurrentStudyFromSubdomain()

  // Fetch study details
  const { data: study, isLoading, error } = useQuery({
    queryKey: ['study', subdomainStudyId],
    queryFn: async () => {
      if (!subdomainStudyId) return null
      const response = await api.get(`/studies/${subdomainStudyId}`)
      return response.data as Study
    },
    enabled: !!subdomainStudyId && isAuthenticated,
  })

  // Set current study when loaded
  useEffect(() => {
    if (study && study.studyId !== currentStudy?.studyId) {
      setCurrentStudy(study)
    }
  }, [study, currentStudy?.studyId, setCurrentStudy])

  // Handle study not found - redirect to main app
  if (!isLoading && error && subdomainStudyId && isAuthenticated) {
    return (
      <AuthenticatedLayout>
        <div className="flex flex-col items-center justify-center py-12">
          <h2 className="text-2xl font-bold mb-4">Study Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The study "{subdomainStudyId}" does not exist or you don't have access.
          </p>
          <button
            className="text-primary hover:underline"
            onClick={() => navigateToMainApp('/studies')}
          >
            Browse Studies
          </button>
        </div>
      </AuthenticatedLayout>
    )
  }

  return <>{children}</>
}

// Find the first page (not menu) in navigation
function findFirstPageKey(items: NavigationItem[]): string | null {
  for (const item of items) {
    if (item.path) {
      // Extract pageKey from path (e.g., "/study/:studyId/study-dashboard" -> "study-dashboard")
      const match = item.path.match(/\/([^/]+)$/)
      if (match) return match[1]
    }
    if (item.children) {
      const found = findFirstPageKey(item.children)
      if (found) return found
    }
  }
  return null
}

// Component to redirect to first page in study navigation (for subdomain)
function StudyHomeRedirect() {
  const { studyNavigation, isLoadingStudyNav } = useNavigationStore()
  const firstPageKey = findFirstPageKey(studyNavigation)

  // While loading, show nothing (SubdomainStudyLoader handles loading state)
  if (isLoadingStudyNav) {
    return null
  }

  // Redirect to first page or fallback to study-dashboard
  const targetPage = firstPageKey || 'study-dashboard'
  return <Navigate to={`/${targetPage}`} replace />
}

// Component to redirect to first page in study navigation (for main domain path-based routing)
function MainDomainStudyHomeRedirect() {
  const { studyId } = useParams<{ studyId: string }>()
  const { studyNavigation, isLoadingStudyNav } = useNavigationStore()
  const firstPageKey = findFirstPageKey(studyNavigation)

  // While loading, show nothing
  if (isLoadingStudyNav) {
    return null
  }

  // Redirect to first page or fallback to study-dashboard
  const targetPage = firstPageKey || 'study-dashboard'
  return <Navigate to={`/study/${studyId}/${targetPage}`} replace />
}

// Component to redirect main-domain-only routes to main app
function MainDomainRedirect({ path }: { path: string }) {
  useEffect(() => {
    navigateToMainApp(path)
  }, [path])
  return null
}

// Routes for study subdomain (abc-101.localhost)
function StudySubdomainRoutes() {
  return (
    <SubdomainStudyLoader>
      <Suspense fallback={<PageLoadingFallback />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />

        {/* Settings */}
        <Route
          path="/settings"
          element={
            <AuthenticatedLayout>
              <StudySettingsPage />
            </AuthenticatedLayout>
          }
        />

        {/* Issues routes (addon feature - requires issues_tracker enabled) */}
        <Route
          path="/issues"
          element={
            <AuthenticatedLayout>
              <FeatureGate feature={FEATURE_KEYS.ISSUES_TRACKER}>
                <IssuesListPage />
              </FeatureGate>
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/issues/private-sticky"
          element={
            <AuthenticatedLayout>
              <FeatureGate feature={FEATURE_KEYS.ISSUES_TRACKER}>
                <SelfNotesPage />
              </FeatureGate>
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/issues/query-management"
          element={
            <AuthenticatedLayout>
              <FeatureGate feature={FEATURE_KEYS.ISSUES_TRACKER}>
                <QueryTrackingPage />
              </FeatureGate>
            </AuthenticatedLayout>
          }
        />

        {/* Patient Profile — unified single page with subject dropdown */}
        <Route
          path="/patient-profile"
          element={
            <AuthenticatedLayout>
              <FeatureGate feature={FEATURE_KEYS.PATIENT_PROFILE}>
                <PatientProfilePage />
              </FeatureGate>
            </AuthenticatedLayout>
          }
        />

        {/* Safety Narratives — tracker dashboard (addon feature) */}
        <Route
          path="/safety-narratives"
          element={
            <AuthenticatedLayout>
              <FeatureGate feature={FEATURE_KEYS.NARRATIVE}>
                <NarrativeTrackerPage />
              </FeatureGate>
            </AuthenticatedLayout>
          }
        />

        {/* User Account (Profile, Security, Preferences) - same on all domains */}
        <Route
          path="/account"
          element={
            <AuthenticatedLayout>
              <SettingsPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/account/:tab"
          element={
            <AuthenticatedLayout>
              <SettingsPage />
            </AuthenticatedLayout>
          }
        />

        {/* Redirect main-domain-only routes back to main app */}
        <Route path="/studies" element={<MainDomainRedirect path="/studies" />} />
        <Route path="/dashboard" element={<MainDomainRedirect path="/dashboard" />} />
        <Route path="/admin/*" element={<MainDomainRedirect path="/admin" />} />

        {/* Dynamic page route - handles ALL custom pages configured in study settings */}
        <Route
          path="/:pageKey"
          element={
            <AuthenticatedLayout>
              <StudyPageView />
            </AuthenticatedLayout>
          }
        />

        {/* Default redirect to first page in study navigation */}
        <Route
          path="/"
          element={
            <AuthenticatedLayout>
              <StudyHomeRedirect />
            </AuthenticatedLayout>
          }
        />
      </Routes>
      </Suspense>
    </SubdomainStudyLoader>
  )
}

// Routes for main domain (localhost)
function MainDomainRoutes() {
  return (
    <Suspense fallback={<PageLoadingFallback />}>
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />

      {/* Admin dashboard - system admin only */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requiredRole="system_admin">
            <AppShell>
              <HomePage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/studies"
        element={
          <AuthenticatedLayout>
            <StudyListPage />
          </AuthenticatedLayout>
        }
      />

      {/* Study-specific routes (path-based fallback) */}
      <Route
        path="/study/:studyId"
        element={
          <StudyLoader>
            <AuthenticatedLayout>
              <MainDomainStudyHomeRedirect />
            </AuthenticatedLayout>
          </StudyLoader>
        }
      />

      <Route
        path="/study/:studyId/settings"
        element={
          <StudyLoader>
            <AuthenticatedLayout>
              <StudySettingsPage />
            </AuthenticatedLayout>
          </StudyLoader>
        }
      />

      {/* Issues routes (addon feature - requires issues_tracker enabled) */}
      <Route
        path="/study/:studyId/issues"
        element={
          <StudyLoader>
            <AuthenticatedLayout>
              <FeatureGate feature={FEATURE_KEYS.ISSUES_TRACKER}>
                <IssuesListPage />
              </FeatureGate>
            </AuthenticatedLayout>
          </StudyLoader>
        }
      />
      <Route
        path="/study/:studyId/issues/private-sticky"
        element={
          <StudyLoader>
            <AuthenticatedLayout>
              <FeatureGate feature={FEATURE_KEYS.ISSUES_TRACKER}>
                <SelfNotesPage />
              </FeatureGate>
            </AuthenticatedLayout>
          </StudyLoader>
        }
      />
      <Route
        path="/study/:studyId/issues/query-management"
        element={
          <StudyLoader>
            <AuthenticatedLayout>
              <FeatureGate feature={FEATURE_KEYS.ISSUES_TRACKER}>
                <QueryTrackingPage />
              </FeatureGate>
            </AuthenticatedLayout>
          </StudyLoader>
        }
      />

      {/* Patient Profile — unified single page with subject dropdown */}
      <Route
        path="/study/:studyId/patient-profile"
        element={
          <StudyLoader>
            <AuthenticatedLayout>
              <FeatureGate feature={FEATURE_KEYS.PATIENT_PROFILE}>
                <PatientProfilePage />
              </FeatureGate>
            </AuthenticatedLayout>
          </StudyLoader>
        }
      />

      {/* Safety Narratives — tracker dashboard (addon feature) */}
      <Route
        path="/study/:studyId/safety-narratives"
        element={
          <StudyLoader>
            <AuthenticatedLayout>
              <FeatureGate feature={FEATURE_KEYS.NARRATIVE}>
                <NarrativeTrackerPage />
              </FeatureGate>
            </AuthenticatedLayout>
          </StudyLoader>
        }
      />

      {/* Dynamic page route - handles custom pages configured in study settings */}
      <Route
        path="/study/:studyId/:pageKey"
        element={
          <StudyLoader>
            <AuthenticatedLayout>
              <StudyPageView />
            </AuthenticatedLayout>
          </StudyLoader>
        }
      />

      {/* User Account (Profile, Security, Preferences) */}
      <Route
        path="/account"
        element={
          <AuthenticatedLayout>
            <SettingsPage />
          </AuthenticatedLayout>
        }
      />
      <Route
        path="/account/:tab"
        element={
          <AuthenticatedLayout>
            <SettingsPage />
          </AuthenticatedLayout>
        }
      />

      {/* Admin routes - system admin only */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute requiredRole="system_admin">
            <AppShell>
              <AdminRoutes />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* Redirects - send users to studies list */}
      <Route path="/" element={<Navigate to="/studies" replace />} />
      <Route path="*" element={<Navigate to="/studies" replace />} />
    </Routes>
    </Suspense>
  )
}

// Main router that decides between subdomain and path-based routing
function AppRoutes() {
  // Track page views for analytics (Phase 16)
  useAnalyticsTracking()

  // Check if we're on a study subdomain
  const onStudySubdomain = isOnStudySubdomain()

  if (onStudySubdomain) {
    return <StudySubdomainRoutes />
  }

  return <MainDomainRoutes />
}

function App() {
  const [showPreloader, setShowPreloader] = useState(true)
  const handlePreloaderComplete = useCallback(() => setShowPreloader(false), [])

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system">
        <TooltipProvider>
          {showPreloader && (
            <Preloader onComplete={handlePreloaderComplete} />
          )}
          <Router>
            <AuthProvider>
              {/* Hydrate preferences from backend on boot, sync theme/sidebar */}
              <PreferencesHydrator />
              {/* Validate persisted study exists - clears stale references */}
              <PersistedStudyValidator />
              <AppRoutes />
              {/* Global annotation components - can be triggered from anywhere */}
              <CreateAnnotationModal />
              <IssueDetailPanel />
            </AuthProvider>
          </Router>
          <Toaster />
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

// Admin routes
function AdminRoutes() {
  return (
    <Routes>
      <Route path="users" element={<UserManagementPage />} />
      <Route path="roles" element={<RoleManagementPage />} />
      <Route path="security" element={<SecuritySettingsPage />} />
      <Route path="branding" element={<BrandingPage />} />
      <Route path="system" element={<SystemMonitoringPage />} />
      <Route path="update-change-control" element={<UpdateChangeControlPage />} />
      <Route path="audit-logs" element={<AuditLogsPage />} />
      <Route path="notifications" element={<NotificationChannelsPage />} />
      <Route path="column-labels" element={<ColumnLabelsPage />} />
      <Route path="modules" element={<ModuleLibraryPage />} />
      <Route path="r-packages" element={<RPackageManagerPage />} />
      <Route path="r-functions" element={<RFunctionLibraryPage />} />
      <Route path="external-files" element={<ExternalFilesPage />} />
      <Route path="compliance-docs" element={<ComplianceDocsPage />} />
      <Route path="ct-library" element={<CTLibraryPage />} />
      <Route path="coding-library" element={<CodingLibraryPage />} />
      <Route path="ai-suite" element={<AISuitePage />} />
      <Route path="narrative-settings" element={<NarrativeSettingsPage />} />
      <Route path="data-upload" element={<DataUploadPage />} />
      <Route path="user-guide" element={<UserGuidePage />} />
      {/* Create study - simple form that redirects to settings */}
      <Route path="studies/new" element={<CreateStudyPage />} />
      {/* Admin studies list redirects to main studies page */}
      <Route path="studies" element={<Navigate to="/studies" replace />} />
      <Route
        path="*"
        element={
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold mb-4">Administration</h2>
            <p className="text-muted-foreground">
              Use the navigation to access admin features.
            </p>
          </div>
        }
      />
    </Routes>
  )
}

export default App
